import { AxiosResponse } from "axios";
import { CommonActionType } from "models/actionTypes/commonActionTypes";
import { LoginActionType } from "models/actionTypes/loginActionTypes";
import { AuthEnums } from "models/enums";
import { BusinessUnitManagerSettings, Person } from "models/stateTypes/authState";
import { ThunkAction, ThunkDispatch } from "redux-thunk";
import {
  AuthService,
  BusinessUnitService,
  CompanyService,
  ProfileService,
  ReceiptPaymentMethodService,
  ReceiptService,
  SupplierService,
  UserService,
} from "services";
import { AppState } from "store";
import {
  loadAllDimensions,
  loadDimensions,
  loadDimensionSuggestionsAsync,
  setCompanyAccountingDoneUntilMonth,
  setCompanyLastReconciliation,
} from "store/actions/commonActions";
import { emptyTodoSummary, emptyTodos } from "store/actions/todoActions";
import { asCompany, displayErrorMessage } from "store/actions/utilActions";
import { ReduxConstants, RouteConstants } from "utils/constants";
import { WintSuperkollAPIModel as ApiModels, WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { AuthHelper, LocalizationHelperInstance, SessionHelper } from "utils/helpers";
import { TranslationKeys } from "translations";
import { TodoActionType } from "models/actionTypes/todoActionTypes";
import { resetCachedCharts } from "./cachedChartsActions";
import { CachedChartActionTypes } from "models/actionTypes/cachedChartActionTypes";
import { ONBOARDING_SESSION_KEY_PREFIX } from "serviceHooks/useAPIClientHelper";

export const loginSuccess = (user: Person, fromSession?: boolean): LoginActionType => {
  return {
    type: ReduxConstants.AuthActions.LOGIN_SUCCESS,
    payload: { user: user, fromSession: fromSession },
  };
};

export const loginError = (): LoginActionType => {
  return {
    type: ReduxConstants.AuthActions.LOGIN_ERROR,
  };
};

export const logout = (): LoginActionType => {
  // The onboarding session is not this session's to end: it was minted against an invite, and a
  // visitor part-way through onboarding in this tab would otherwise lose it to a Wint logout.
  SessionHelper.clearSession([], [ONBOARDING_SESSION_KEY_PREFIX]);
  return {
    type: ReduxConstants.AuthActions.LOGOUT,
  };
};

export const setFetchingRefreshToken = (isRefreshing: boolean): LoginActionType => {
  return {
    type: ReduxConstants.AuthActions.SET_FETCHING_REFRESH_TOKEN,
    payload: isRefreshing,
  };
};

export const loadCompanies = (companies: ApiModels.Company[]): LoginActionType => {
  return {
    type: ReduxConstants.AuthActions.LOAD_COMPANIES,
    payload: companies,
  };
};

export const loadCompanyData = (company: ApiModels.Company): LoginActionType => {
  return {
    type: ReduxConstants.AuthActions.LOAD_COMPANY_DATA,
    payload: company,
  };
};

export const setSelectedCompany = (company: ApiModels.Company): LoginActionType => {
  return {
    type: ReduxConstants.AuthActions.SET_SELECTED_COMPANY,
    payload: { company },
  };
};

export const setProfilePicture = (profileImage: ApiModels.ProfileImage): LoginActionType => {
  return {
    type: ReduxConstants.AuthActions.SET_PROFILE_PICTURE,
    payload: profileImage,
  };
};

export const addPaymentMethod = (paymentMethod: ApiModels.ReceiptPaymentMethod): LoginActionType => {
  return {
    type: ReduxConstants.AuthActions.ADD_PAYMENT_METHOD,
    payload: paymentMethod,
  };
};

export const updatePaymentMethod = (paymentMethod: ApiModels.ReceiptPaymentMethod): LoginActionType => {
  return {
    type: ReduxConstants.AuthActions.UPDATE_PAYMENT_METHOD,
    payload: paymentMethod,
  };
};

export const loadPaymentMethods = (
  paymentMethods: ApiModels.ReceiptPaymentMethod[],
  wintCardPaymentMethods: ApiModels.ReceiptPaymentMethod[]
): LoginActionType => {
  return {
    type: ReduxConstants.AuthActions.LOAD_PAYMENT_METHODS,
    payload: { paymentMethods: paymentMethods, wintCardPaymentMethods: wintCardPaymentMethods },
  };
};

export const loadProfileToggle = (profileToggles: ApiModels.ProfileToggles): LoginActionType => {
  return {
    type: ReduxConstants.AuthActions.LOAD_PROFILE_TOGGLES,
    payload: profileToggles,
  };
};

export const addIntroduction = (id: string): LoginActionType => {
  return {
    type: ReduxConstants.AuthActions.ADD_INTRODUCTION,
    payload: id,
  };
};

export const addHelp = (id: string): LoginActionType => {
  return {
    type: ReduxConstants.AuthActions.ADD_HELP,
    payload: id,
  };
};

export const setProfileName = (name: string): LoginActionType => {
  return {
    type: ReduxConstants.AuthActions.SET_PROFILE_NAME,
    payload: name,
  };
};

export const loadBusinessUnitManagerSettings = (
  businessUnitManagerSettings: BusinessUnitManagerSettings
): LoginActionType => {
  return {
    type: ReduxConstants.AuthActions.LOAD_BUSINESS_UNIT_MANAGER_SETTINGS,
    payload: businessUnitManagerSettings,
  };
};

export const updateBusinessUnitManagerSettings = (
  businessUnitManagerSettings: BusinessUnitManagerSettings
): LoginActionType => {
  return {
    type: ReduxConstants.AuthActions.UPDATE_BUSINESS_UNIT_MANAGER_SETTINGS,
    payload: businessUnitManagerSettings,
  };
};

/**
 * @deprecated This Redux action is deprecated. Create a new useLoadCompanyData hook instead.
 *
 * Migration guide:
 * 1. Replace loadCompanyDataAsync() dispatch calls with useLoadCompanyData hook
 * 2. Use the hook in React components or custom hooks
 * 3. Handle Redux state updates through the hook or custom Redux actions
 *
 * @see useLoadCompanyData - The replacement hook (to be created)
 * @see https://app.clickup.com/4523827/v/dc/4a1tk-21632/4a1tk-8592
 *
 * @example
 * // Old way (deprecated):
 * import { loadCompanyDataAsync } from "store/actions/authActions";
 *
 * dispatch(loadCompanyDataAsync(companyId));
 *
 * @example
 * // New way (recommended):
 * import useLoadCompanyData from "serviceHooks/useLoadCompanyData";
 *
 * function MyComponent() {
 *   const { loadCompanyData } = useLoadCompanyData();
 *   loadCompanyData(companyId);
 * }
 */
export const loadCompanyDataAsync = (
  companyId?: number
): ThunkAction<Promise<void>, AppState, null, LoginActionType | CommonActionType> => {
  return (dispatch, getState) => {
    return CompanyService.getCompanyData(companyId ?? getState().auth.selectedCompanyId ?? 0).then((res) => {
      dispatch(loadCompanyData(res));
    });
  };
};

export const setAuthTokens = (authTokens: ApiModels.AuthTokens): LoginActionType => {
  return {
    type: ReduxConstants.AuthActions.SET_AUTH_TOKENS,
    payload: authTokens,
  };
};

export const setSelectedCompanyId = (companyId: number): LoginActionType => {
  return {
    type: ReduxConstants.AuthActions.SET_SELECTED_COMPANY_ID,
    payload: companyId,
  };
};

export const setHasMissingPaymentDetails = (hasMissingPaymentDetails: boolean): LoginActionType => {
  return {
    type: ReduxConstants.AuthActions.SET_HAS_MISSING_PAYMENT_DETAILS,
    payload: hasMissingPaymentDetails,
  };
};

/**
 * @deprecated This Redux action is deprecated. Create a new useSelectCompany hook instead.
 *
 * Migration guide:
 * 1. Replace selectCompanyAsync() dispatch calls with useSelectCompany hook
 * 2. Use the hook in React components or custom hooks
 * 3. Handle Redux state updates through the hook or custom Redux actions
 *
 * @see useSelectCompany - The replacement hook (to be created)
 * @see https://app.clickup.com/4523827/v/dc/4a1tk-21632/4a1tk-8592
 *
 * @example
 * // Old way (deprecated):
 * import { selectCompanyAsync } from "store/actions/authActions";
 *
 * dispatch(selectCompanyAsync(companyId));
 *
 * @example
 * // New way (recommended):
 * import useSelectCompany from "serviceHooks/useSelectCompany";
 *
 * function MyComponent() {
 *   const { selectCompany } = useSelectCompany();
 *   selectCompany(companyId);
 * }
 */
export const selectCompanyAsync = (
  companyId: number
): ThunkAction<
  Promise<void>,
  AppState,
  null,
  LoginActionType | CommonActionType | TodoActionType | CachedChartActionTypes
> => {
  return (dispatch, getState) => {
    dispatch(resetCachedCharts());
    dispatch(emptyTodos());
    dispatch(emptyTodoSummary());
    dispatch(asCompany(companyId, emptyTodos()));
    dispatch(setSelectedCompanyId(companyId));
    return new Promise<void>((resolve) => {
      Promise.all([CompanyService.getCompanyData(companyId)]).then(([company]) => {
        if (AuthHelper.isSelectedCompany(companyId)) {
          dispatch(setSelectedCompany(company));
          processCompanyDetails(company).map((action) => dispatch(action));
          dispatch(loadAllDimensions());
          dispatch(loadDimensionSuggestionsAsync());
          dispatch(loadBusinessUnitManagerSettingsAsync());
          dispatch(getTaxAgentAsync());
          dispatch(setCompanyAccountingDoneUntilMonth());
          dispatch(setCompanyLastReconciliation());
        }
        resolve();
      });
    });
  };
};

/**
 * @deprecated This Redux action is deprecated. Create a new useLoadCompanies hook instead.
 *
 * Migration guide:
 * 1. Replace loadCompaniesAsync() dispatch calls with useLoadCompanies hook
 * 2. Use the hook in React components or custom hooks
 * 3. Handle Redux state updates through the hook or custom Redux actions
 *
 * @see useLoadCompanies - The replacement hook (to be created)
 * @see https://app.clickup.com/4523827/v/dc/4a1tk-21632/4a1tk-8592
 *
 * @example
 * // Old way (deprecated):
 * import { loadCompaniesAsync } from "store/actions/authActions";
 *
 * dispatch(loadCompaniesAsync());
 *
 * @example
 * // New way (recommended):
 * import useLoadCompanies from "serviceHooks/useLoadCompanies";
 *
 * function MyComponent() {
 *   const { loadCompanies } = useLoadCompanies();
 *   loadCompanies();
 * }
 */
export const loadCompaniesAsync = (): ThunkAction<Promise<ApiModels.Company[]>, AppState, null, LoginActionType> => {
  return (dispatch) => {
    return new Promise<ApiModels.Company[]>((resolve) => {
      CompanyService.getCompanies().then((companies) => {
        dispatch(loadCompanies(companies));
        resolve(companies);
      });
    });
  };
};

/**
 * @deprecated This Redux action is deprecated. Use useReceiptPaymentMethodService hook instead.
 *
 * Migration guide:
 * 1. Replace loadReceiptPaymentMethodsAsync() dispatch calls with useReceiptPaymentMethodService hook
 * 2. Use the getAll() function from the hook to fetch payment methods
 * 3. Handle Redux state updates through the hook or custom Redux actions
 * 4. Move the filtering logic to the component or create a custom hook
 *
 * @see useReceiptPaymentMethodService - The replacement hook for receipt payment method functionality
 * @see https://app.clickup.com/4523827/v/dc/4a1tk-21632/4a1tk-8592
 *
 * @example
 * // Old way (deprecated):
 * import { loadReceiptPaymentMethodsAsync } from "store/actions/authActions";
 *
 * dispatch(loadReceiptPaymentMethodsAsync());
 *
 * @example
 * // New way (recommended):
 * import useReceiptPaymentMethodService from "serviceHooks/useReceiptPaymentMethodService";
 *
 * function useLoadReceiptPaymentMethods() {
 *   const { getAll } = useReceiptPaymentMethodService();
 *
 *   const loadPaymentMethods = async () => {
 *     const paymentMethods = await getAll();
 *     // Handle filtering and Redux state updates here
 *   };
 * }
 */

export const loadReceiptPaymentMethodsAsync = (): ThunkAction<
  Promise<ApiModels.ReceiptPaymentMethod[]>,
  AppState,
  null,
  LoginActionType
> => {
  return (dispatch) => {
    return new Promise<ApiModels.ReceiptPaymentMethod[]>((resolve) => {
      ReceiptPaymentMethodService.getAll().then((res) => {
        dispatch(
          loadPaymentMethods(
            res?.filter(
              (pm) =>
                pm.type !== WintSuperkollAPIModel.ReceiptPaymentMethodType.WintCard &&
                pm.type !== WintSuperkollAPIModel.ReceiptPaymentMethodType.MissingDocument
            ),
            res?.filter((pm) => pm.type === WintSuperkollAPIModel.ReceiptPaymentMethodType.WintCard) ?? []
          )
        );
        resolve(res);
      });
    });
  };
};

/**
 * @deprecated This Redux action is deprecated. Create a new useLoadBusinessUnitManagerSettings hook instead.
 *
 * Migration guide:
 * 1. Replace loadBusinessUnitManagerSettingsAsync() dispatch calls with useLoadBusinessUnitManagerSettings hook
 * 2. Use the hook in React components or custom hooks
 * 3. Handle Redux state updates through the hook or custom Redux actions
 *
 * @see useLoadBusinessUnitManagerSettings - The replacement hook (to be created)
 * @see https://app.clickup.com/4523827/v/dc/4a1tk-21632/4a1tk-8592
 *
 * @example
 * // Old way (deprecated):
 * import { loadBusinessUnitManagerSettingsAsync } from "store/actions/authActions";
 *
 * dispatch(loadBusinessUnitManagerSettingsAsync());
 *
 * @example
 * // New way (recommended):
 * import useLoadBusinessUnitManagerSettings from "serviceHooks/useLoadBusinessUnitManagerSettings";
 *
 * function MyComponent() {
 *   const { loadBusinessUnitManagerSettings } = useLoadBusinessUnitManagerSettings();
 *   loadBusinessUnitManagerSettings();
 * }
 */
export const loadBusinessUnitManagerSettingsAsync = (): ThunkAction<
  Promise<BusinessUnitManagerSettings>,
  AppState,
  null,
  LoginActionType
> => {
  return (dispatch, getState) => {
    return new Promise<BusinessUnitManagerSettings>((resolve) => {
      if (getState().auth.selectedCompany.loggedInUserIsBusinessUnitManagerWithSalaryRights) {
        const personId = getState().auth.loggedInUser.personId;
        BusinessUnitService.searchBusinessUnits().then((res) => {
          const manageableEmployeeIds = res
            // eslint-disable-next-line eqeqeq --- supressed to avoid regression testing if changed
            .filter((unit) => unit.managerId == personId && unit.managerHasSalaryRights)
            .flatMap((unit) => unit.members?.map((m) => m.employeeId) ?? []);

          const settings = new BusinessUnitManagerSettings();
          settings.manageableEmployees = manageableEmployeeIds;
          settings.manageableEmployeesLoaded = true;
          dispatch(loadBusinessUnitManagerSettings(settings));
          resolve(settings);
        });
      } else {
        const settings = new BusinessUnitManagerSettings();
        settings.manageableEmployeesLoaded = true;
        resolve(settings);
      }
    });
  };
};

export const loginAsync = (
  requestId: string,
  auth: ApiModels.AuthPost | ApiModels.BankIdAuthPost | string | undefined,
  loginMethod: AuthEnums.LoginMethod,
  cb: (qrStartCode: string, time: string, qrAuthCode: string) => void = (
    qrStartCode: string,
    time: string,
    qrAuthCode: string
  ) => {}
): ThunkAction<Promise<number | undefined>, AppState, null, LoginActionType> => {
  return (dispatch, getState) => {
    const isCancelled = () => {
      return getState().common.cancelledRequests.includes(requestId);
    };
    return processLoginResult(
      requestId,
      loginMethod === AuthEnums.LoginMethod.USERNAME_PASSWORD
        ? AuthService.loginUser(auth as ApiModels.AuthPost)
        : loginMethod === AuthEnums.LoginMethod.BANKID_DESKTOP
          ? AuthService.loginBankIdDesktopApp(isCancelled)
          : loginMethod === AuthEnums.LoginMethod.TICKET
            ? AuthService.loginTicket(auth as string)
            : loginMethod === AuthEnums.LoginMethod.QR_CODE
              ? AuthService.loginQrCode(cb, isCancelled)
              : Promise.reject(),
      dispatch,
      getState
    );
  };
};

const processLoginResult = (
  requestId: string,
  result: Promise<AxiosResponse<ApiModels.UserLoginInfo>>,
  dispatch: ThunkDispatch<AppState, null, LoginActionType>,
  getState: () => AppState
) => {
  return new Promise<number | undefined>((resolve, reject) => {
    result
      .then((res) => res.data)
      .then((loggedInUser: ApiModels.UserLoginInfo) => {
        processLoginResultSuccess(requestId, loggedInUser, dispatch, resolve, getState);
      })
      .catch(() => {
        processLoginResultError(requestId, dispatch, reject, getState);
      });
  });
};

const processLoginResultSuccess = (
  requestId: string,
  loggedInUser: ApiModels.UserLoginInfo,
  dispatch: ThunkDispatch<AppState, null, LoginActionType>,
  resolve: (loginState?: number) => void,
  getState: () => AppState
) => {
  if (authRequestNotCancelled(getState, requestId)) {
    if (
      loggedInUser.state === null ||
      loggedInUser.state === undefined ||
      loggedInUser.state === WintSuperkollAPIModel.LoginState.Success
    ) {
      const person = AuthService.mapLoggedInUserToPerson(loggedInUser);
      SessionHelper.saveToSession("user_session", person);
      dispatch(loginSuccess(person));
    } else {
      const errorMessage = (): string => {
        switch (loggedInUser.state) {
          case WintSuperkollAPIModel.LoginState.WrongUsernameOrPassword:
          case WintSuperkollAPIModel.LoginState.ForceLoginWithBankId: //Todo: Display company names
          case WintSuperkollAPIModel.LoginState.AccountLocked:
          case WintSuperkollAPIModel.LoginState.NoCompanies:
          case WintSuperkollAPIModel.LoginState.Timeout:
          case WintSuperkollAPIModel.LoginState.IncorrectPersonalNumber:
          case WintSuperkollAPIModel.LoginState.UnknownBankIdError:
            return LocalizationHelperInstance.getFormattedMessage(`Login.Result_${loggedInUser.state}`);
          default:
            return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Errors.SomethingWentWrongTryAgain);
        }
      };
      dispatch(loginError());
      dispatch(displayErrorMessage(errorMessage()));
    }
    resolve(loggedInUser.state);
  }

  resolve(0);
};

const processLoginResultError = (
  requestId: string,
  dispatch: ThunkDispatch<AppState, null, LoginActionType>,
  reject: (loginState?: number) => void,
  getState: () => AppState
) => {
  if (authRequestNotCancelled(getState, requestId)) {
    dispatch(loginError());
    dispatch(
      displayErrorMessage(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Errors.SomethingWentWrongInLogin)
      )
    );
    reject(-1);
  }
};

export const loadProfilePicture = (profileId: number): ThunkAction<Promise<void>, AppState, null, LoginActionType> => {
  return (dispatch) => {
    return new Promise<void>((resolve) => {
      ProfileService.getProfileImage(profileId).then((profileImage) => {
        dispatch(setProfilePicture(profileImage));
        resolve();
      });
    });
  };
};

/**
 * @deprecated This Redux action is deprecated. Create a new useLoadProfileToggle hook instead.
 *
 * Migration guide:
 * 1. Replace loadProfileToggleAsync() dispatch calls with useLoadProfileToggle hook
 * 2. Use the hook in React components or custom hooks
 * 3. Handle Redux state updates through the hook or custom Redux actions
 *
 * @see useLoadProfileToggle - The replacement hook (to be created)
 * @see https://app.clickup.com/4523827/v/dc/4a1tk-21632/4a1tk-8592
 *
 * @example
 * // Old way (deprecated):
 * import { loadProfileToggleAsync } from "store/actions/authActions";
 *
 * dispatch(loadProfileToggleAsync());
 *
 * @example
 * // New way (recommended):
 * import useLoadProfileToggle from "serviceHooks/useLoadProfileToggle";
 *
 * function MyComponent() {
 *   const { loadProfileToggle } = useLoadProfileToggle();
 *   loadProfileToggle();
 * }
 */
export const loadProfileToggleAsync = (): ThunkAction<
  Promise<ApiModels.ProfileToggles>,
  AppState,
  null,
  LoginActionType
> => {
  return (dispatch) => {
    return new Promise<ApiModels.ProfileToggles>((resolve) => {
      UserService.getProfileToggles().then((res) => {
        dispatch(loadProfileToggle(res.data));
        resolve(res.data);
      });
    });
  };
};

const processCompanyDetails = (company: ApiModels.Company): CommonActionType[] => {
  const actions: CommonActionType[] = [
    loadDimensions(
      company.cashReconciliationDimensionTypes ?? [],
      company.cashReconciliationDimensions ?? [],
      ReduxConstants.CommonActions.CASH_RECONCILIATION_DIMENSIONS
    ),
  ];

  return actions;
};

const authRequestNotCancelled = (getState: () => AppState, requestId: string) => {
  return getState().common.cancelledRequests.find((c) => c === requestId) === undefined;
};

/**
 * @deprecated This Redux action is deprecated. Create a new useGetCanPayEmployee hook instead.
 *
 * Migration guide:
 * 1. Replace getCanPayEmployeeAsync() dispatch calls with useGetCanPayEmployee hook
 * 2. Use the hook in React components or custom hooks
 * 3. Handle Redux state updates through the hook or custom Redux actions
 *
 * @see useGetCanPayEmployee - The replacement hook (to be created)
 * @see https://app.clickup.com/4523827/v/dc/4a1tk-21632/4a1tk-8592
 *
 * @example
 * // Old way (deprecated):
 * import { getCanPayEmployeeAsync } from "store/actions/authActions";
 *
 * dispatch(getCanPayEmployeeAsync());
 *
 * @example
 * // New way (recommended):
 * import useGetCanPayEmployee from "serviceHooks/useGetCanPayEmployee";
 *
 * function MyComponent() {
 *   const { getCanPayEmployee } = useGetCanPayEmployee();
 *   getCanPayEmployee();
 * }
 */
export const getCanPayEmployeeAsync = (): ThunkAction<Promise<void>, AppState, null, LoginActionType> => {
  return async (dispatch) => {
    ReceiptService.getReceiptsCanBePayedOut().then((res) => {
      dispatch(setCanPayEmployee(res.data));
    });
  };
};

export const setCanPayEmployee = (canPay: boolean): LoginActionType => {
  return {
    type: ReduxConstants.AuthActions.SET_CAN_PAY_EMPLOYEE,
    payload: canPay,
  };
};

export const setShowAllCompaniesTodoOverview = (show: boolean): LoginActionType => {
  return {
    type: ReduxConstants.AuthActions.SET_SHOW_ALL_COMPANIES_TODO_OVERVIEW,
    payload: show,
  };
};

export const LoadTaxAgentOfCompany = (taxAgent?: ApiModels.IncomingInvoiceSupplierInfo): LoginActionType => {
  return {
    type: ReduxConstants.AuthActions.LOAD_TAX_AGENT_OF_COMPANY,
    payload: taxAgent,
  };
};

export const hideEULA = (): LoginActionType => {
  const userSession = SessionHelper.getSessionItem<Person>("user_session");
  userSession.unsignedEula = false;
  SessionHelper.saveToSession("user_session", userSession);

  return {
    type: ReduxConstants.AuthActions.HIDE_EULA,
  };
};

/**
 * @deprecated This Redux action is deprecated. Create a new useGetTaxAgent hook instead.
 *
 * Migration guide:
 * 1. Replace getTaxAgentAsync() dispatch calls with useGetTaxAgent hook
 * 2. Use the hook in React components or custom hooks
 * 3. Handle Redux state updates through the hook or custom Redux actions
 *
 * @see useGetTaxAgent - The replacement hook (to be created)
 * @see https://app.clickup.com/4523827/v/dc/4a1tk-21632/4a1tk-8592
 *
 * @example
 * // Old way (deprecated):
 * import { getTaxAgentAsync } from "store/actions/authActions";
 *
 * dispatch(getTaxAgentAsync());
 *
 * @example
 * // New way (recommended):
 * import useGetTaxAgent from "serviceHooks/useGetTaxAgent";
 *
 * function MyComponent() {
 *   const { getTaxAgent } = useGetTaxAgent();
 *   getTaxAgent();
 * }
 */
export const getTaxAgentAsync = (): ThunkAction<
  Promise<ApiModels.IncomingInvoiceSupplierInfo | undefined>,
  AppState,
  null,
  LoginActionType
> => {
  return (dispatch) => {
    return new Promise<ApiModels.IncomingInvoiceSupplierInfo | undefined>((resolve) => {
      const canSeeSuppliers = CompanyService.hasRelations([
        AuthEnums.PersonCompanyRelationType.Admin,
        AuthEnums.PersonCompanyRelationType.IncomingInvoicesAdmin,
        AuthEnums.PersonCompanyRelationType.IncomingInvoicesCertifyAdmin,
        AuthEnums.PersonCompanyRelationType.IncomingInvoices,
        AuthEnums.PersonCompanyRelationType.IncomingInvoicesOverview,
        AuthEnums.PersonCompanyRelationType.IncomingInvoicesSignee,
        AuthEnums.PersonCompanyRelationType.Revisor,
      ]);

      if (!canSeeSuppliers) {
        dispatch(LoadTaxAgentOfCompany(undefined));
        return;
      }

      SupplierService.searchSupplierByName(RouteConstants.TaxAgentName).then((res) => {
        const taxAgents = res.find(
          (supplier) => supplier.name?.toLowerCase() === RouteConstants.TaxAgentName.toLowerCase()
        );

        if (taxAgents && taxAgents.id) {
          SupplierService.getSupplier(taxAgents.id as unknown as number)
            .then((result) => {
              dispatch(LoadTaxAgentOfCompany(result));
              resolve(result);
            })
            .catch((e) => {
              console.warn("Error while loading the tax agent");
            });
        } else {
          dispatch(LoadTaxAgentOfCompany(undefined));
          resolve(undefined);
        }
      });
    });
  };
};

export const hideWintCardPromotion = (): LoginActionType => {
  return {
    type: ReduxConstants.AuthActions.HIDE_WINTCARD_PROMOTION,
  };
};

export const updateInvoiceActivationState = (state: WintSuperkollAPIModel.InvoiceActivationState): LoginActionType => {
  return {
    type: ReduxConstants.AuthActions.UPDATE_INVOICE_ACTIVATION_STATE,
    payload: state,
  };
};
